import { message } from 'antd';
import Image from 'next/image';
import React, { memo, useState } from 'react';

import { ImagePreviewImperative } from '@/components/common/ImagePreview/imperative';
import MoreMenu from '@/components/common/MoreMenu';
import { VideoPlayerImperative } from '@/components/common/VideoPlayer/imperative';
import CommonModal from '@/components/UIComponents/CommonModal';
import { useTranslate } from '@/hooks/useTranslate';
import { EventNewID, StatusEnum, UploadTypeEnum } from '@/utils/eventlog/eventNewConfig';
import { trackEvent } from '@/utils/eventlog/logAdapter';
import { downloadStreamFile } from '@/utils/fileUtil';

import EditableTitle from '../../../common/EditableTitle';
import { deleteVideo, renameVideo } from '../../service';
import styles from './index.module.scss';

export interface AssetCardProps {
  id: number;
  title: string;
  cover?: string;
  type: 'image' | 'video';
  duration?: number;
  selected?: boolean;
  fileUrl?: string;
  onDeleted?: () => void;
  onRenamed?: () => void;
}

export const MemorizeImage = memo(
  ({ cover, alt, className }: { cover: string; alt?: string; className?: string }) => {
    const [isLoading, setIsLoading] = useState(true);

    return (
      <div className="relative w-full h-full">
        {/* 这里是 CSS Loading 动画，无需任何图片资源 */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-[#f3f4f6] z-[10]">
            <span className="w-4 h-4 border-2 border-[#e5e7eb] border-t-[#3b82f6] rounded-full animate-spin" />
          </div>
        )}
        <Image
          src={cover || '/images/default-cover-video.png'}
          alt={alt || 'memorize-image'}
          fill
          loading="lazy"
          quality={60}
          unoptimized
          className={`${styles.coverImage} transition-all duration-300 ease-in-out transform opacity-0 scale-95 bg-[#f4f5fe] object-contain ${className || ''}`}
          onLoad={(e: React.SyntheticEvent<HTMLImageElement>) => {
            const img = e.target as HTMLImageElement;
            img.classList.remove('opacity-0', 'scale-95');
            img.classList.add('opacity-100', 'scale-100');
          }}
          onLoadingComplete={() => {
            setIsLoading(false);
          }}
          onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
            const img = e.target as HTMLImageElement;
            img.src = '/images/default-cover-video.png';
            img.classList.remove('opacity-0', 'scale-95');
            img.classList.add('opacity-100', 'scale-100');
          }}
        />
      </div>
    );
  },
);

const AssetCard: React.FC<AssetCardProps> = memo((props) => {
  const {
    id,
    title,
    cover,
    type,
    selected = false,
    fileUrl,
    onDeleted,
    onRenamed,
  } = props;
  const { t } = useTranslate();
  const [currentTitle, setCurrentTitle] = useState(title);
  const [isEditing, setIsEditing] = useState(false);

  const handlePlayClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (type === 'video' && fileUrl) {
      VideoPlayerImperative.show({
        src: fileUrl,
        title: currentTitle,
      });
    } else if (type === 'image') {
      ImagePreviewImperative.show({
        src: fileUrl || cover || '',
        title: currentTitle,
      });
    }
  };

  const handleDownload = () => {
    // todo：暂时找不到哪里调用
    // console.log(1);
    if (fileUrl) {
      // const { featureName, modelName: currentModelName } = getDownloadFeatureInfo(
      //   cloudTaskType,
      //   mediaItems,
      //   modelName,
      // );

      // trackEvent(EventNewID.CREATIONS_DOWNLOAD, {
      //   featureName,
      //   modelName: currentModelName,
      //   download_tier: 'paid',
      // });
      // return;
      downloadStreamFile({
        fileName: title,
        fileUrl,
      });
      trackEvent(EventNewID.CREATIONS_DOWNLOAD_RESULT, {
        result: StatusEnum.SUCCESS,
        mode: UploadTypeEnum.SINGLE,
      });
    }
  };

  const handleDelete = () => {
    CommonModal.show((t) => ({
      width: 557,
      title: t('merror.delete.delete'),
      content: () => (
        <p className="text-center text-[18px] font-medium leading-[24px] tracking-[0.36px] p-[20px]">
          {t('bc.delete.media')}
        </p>
      ),
      cancelText: t('merror.operate.cancel'),
      confirmText: t('merror.delete.delete'),
      onConfirm: async (close) => {
        try {
          const res = await deleteVideo([id]);
          if (res.success) {
            message.success(t('merror.tip.deleteSuccess'));
            onDeleted?.();
            close();
          } else {
            message.error(res.message);
          }
        } catch {
          message.error(t('merror.tip.deleteError'));
        }
      },
    }));
  };

  const handleSaveRename = async (newTitle: string): Promise<boolean> => {
    if (newTitle === currentTitle) {
      return false;
    }

    try {
      const res = await renameVideo({ id, title: newTitle });
      if (res.success) {
        setCurrentTitle(newTitle);
        message.success(t('merror.rename'));
        onRenamed?.();
        return true;
      } else {
        message.warning(res.message || t('merror.rename.fail'));
        return false;
      }
    } catch (error: any) {
      message.error(error.message || t('merror.rename.fail'));
      return false;
    }
  };

  const handleStartRename = () => {
    setIsEditing(true);
  };

  const menuItems = [
    {
      key: 'rename',
      label: t('merror.operate.rename'),
      onClick: handleStartRename,
    },
    {
      key: 'download',
      label: t('merror.setting.download'),
      onClick: handleDownload,
    },
    {
      key: 'delete',
      danger: true,
      label: t('merror.delete.delete'),
      onClick: handleDelete,
    },
  ];

  return (
    <div className={styles.assetCard}>
      <div
        className={`${styles.coverContainer} ${selected ? styles.selected : ''}`}
        onClick={handlePlayClick}
      >
        {cover ? (
          <MemorizeImage cover={cover} alt={currentTitle} />
        ) : (
          <div className={styles.placeholder} />
        )}

        {/* 播放按钮 */}
        {type === 'video' && (
          <div className={styles.playButton}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="36"
              height="36"
              viewBox="0 0 36 36"
              fill="none"
            >
              <circle cx="18" cy="18" r="18" fill="black" fillOpacity="0.24" />
              <path
                d="M25.6475 16.3143C26.8785 17.1008 26.8785 18.8987 25.6475 19.6851L15.9337 25.8911C14.6024 26.7417 12.8569 25.7855 12.857 24.2058L12.857 11.7937C12.857 10.2139 14.6024 9.25775 15.9337 10.1083L25.6475 16.3143Z"
                fill="white"
                fillOpacity="0.9"
              />
            </svg>
          </div>
        )}

        {/* 更多按钮 */}
        <div className={styles.moreButton}>
          <MoreMenu menu={menuItems} />
        </div>
      </div>

      {/* 标题 */}
      <div className={styles.titleContainer}>
        <EditableTitle
          title={currentTitle}
          onSave={handleSaveRename}
          className={styles.title}
          inputClassName={styles.titleInput}
          isEditing={isEditing}
          onEditingChange={setIsEditing}
        />
      </div>
    </div>
  );
});

AssetCard.displayName = 'AssetCard';

export default AssetCard;
